import React from 'react'
import { Routes, Route, Navigate, NavLink, useNavigate, useSearchParams } from 'react-router-dom'
import {
  Building2, FilePlus, LogOut, Cog, Bell
} from 'lucide-react'
import { useAuth } from '../../contexts/AuthContext'
import NotificationBell from '../../components/shared/NotificationBell'
import C2ClientDetail from './pages/C2ClientDetail'
import C2Clients from './pages/C2Clients'
import C2Sinistres from './pages/C2Sinistres'
import C2SinistreDetail from './pages/C2SinistreDetail'
import PoliceDetail from './pages/PoliceDetail'
import PreOuverturesList from './pages/PreOuverturesList'
import PreOuvertureDetail from './pages/PreOuvertureDetail'
import PreDeclarationV4Detail from './pages/PreDeclarationV4Detail'
import GestionnairePreDecl from './pages/GestionnairePreDecl'
import RelancesDashboard from './pages/RelancesDashboard'

// Wrapper to pass URL search params to C2Sinistres
function C2SinistresWrapper() {
  const [searchParams] = useSearchParams()
  const tenantId = searchParams.get('tenant_id')
  return <C2Sinistres forceTenantId={tenantId} />
}

const LOGO_WHITE = '/logo-white.png'

const NAV = [
  { to: '/c2/clients',          icon: Building2, label: 'Clients'           },
  { to: '/c2/pre-declarations', icon: FilePlus,  label: 'Pre-declarations' },
  { to: '/c2/relances',         icon: Bell,      label: 'Relances'         },
]

function Sidebar() {
  const { profile, signOut } = useAuth()
  const navigate = useNavigate()

  async function handleLogout() {
    await signOut()
    navigate('/login', { replace: true })
  }

  return (
    <aside
      className="w-[240px] min-h-screen flex flex-col shrink-0"
      style={{ background: '#0F172A' }}
      data-testid="c2-sidebar"
    >
      {/* Brand */}
      <div className="px-4 py-4 border-b border-white/10">
        <div className="flex items-center justify-between">
          <img 
            src={LOGO_WHITE} 
            alt="C2 Assurances" 
            className="h-8 w-auto"
            data-testid="c2-logo"
          />
          <NotificationBell />
        </div>
        <p className="text-xs text-slate-400 mt-1.5 ml-0.5">Gestionnaire</p>
      </div>

      {/* Navigation */}
      <nav className="flex-1 px-3 py-4 space-y-0.5">
        {NAV.map(({ to, icon: Icon, label }) => (
          <NavLink
            key={to}
            to={to}
            data-testid={`nav-${label.toLowerCase().replace(/[é]/g, 'e').replace(/\s/g, '-')}`}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors ${
                isActive
                  ? 'bg-white/15 text-white font-semibold'
                  : 'text-slate-400 hover:bg-white/8 hover:text-white'
              }`
            }
          >
            <Icon size={16} strokeWidth={1.5} />
            <span>{label}</span>
          </NavLink>
        ))}
      </nav>

      {/* Bottom */}
      <div className="px-3 pb-4 space-y-0.5 border-t border-white/10 pt-3">
        {/* Lien Configuration Clients (admin uniquement) */}
        {(profile?.role === 'master_admin' || profile?.role === 'c2_admin') && (
          <NavLink
            to="/admin/clients"
            className="flex items-center gap-3 px-3 py-2 rounded-md text-sm text-amber-400 hover:bg-amber-500/10 hover:text-amber-300 transition-colors border border-amber-500/30"
            data-testid="nav-admin-config"
          >
            <Cog size={16} strokeWidth={1.5} />
            <span>Master Admin Menu</span>
          </NavLink>
        )}

        {/* User */}
        <div className="px-3 py-2 mt-1">
          <div className="flex items-center gap-2.5 mb-2">
            <div className="w-7 h-7 rounded-full bg-indigo-500/30 flex items-center justify-center text-indigo-300 text-xs font-bold">
              {profile?.prenom?.[0]}{profile?.nom?.[0]}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-semibold text-white truncate">
                {profile?.prenom} {profile?.nom}
              </p>
              <p className="text-xs text-slate-400 truncate">{profile?.role}</p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            data-testid="logout-button"
            className="flex items-center gap-2 text-xs text-slate-400 hover:text-red-400 transition-colors"
          >
            <LogOut size={13} />
            Déconnexion
          </button>
        </div>
        <p className="text-center text-[10px] text-slate-500/50 pt-2">Propriété de C2 Assurances</p>
      </div>
    </aside>
  )
}

export default function C2App() {
  const { profile, loading: authLoading } = useAuth()
  const [tenants, setTenants] = React.useState([])

  React.useEffect(() => {
    // Wait for auth to finish loading AND profile to be available
    if (authLoading || !profile?.id) return
    // Gestionnaires: charger seulement leurs clients. Admin: tous.
    const isAdmin = profile.role === 'master_admin' || profile.role === 'c2_admin'
    const url = isAdmin
      ? `${process.env.REACT_APP_BACKEND_URL}/api/admin/tenants`
      : `${process.env.REACT_APP_BACKEND_URL}/api/c2/my-clients?user_id=${profile.id}&role=${profile.role}`
    fetch(url)
      .then(r => r.json())
      .then(d => setTenants(Array.isArray(d) ? d : d.tenants || d.clients || []))
      .catch(() => {})
  }, [profile, authLoading])

  return (
    <div className="flex min-h-screen bg-slate-50">
      <Sidebar />
      <main className="flex-1 overflow-auto">
        <Routes>
          <Route index element={<Navigate to="clients" replace />} />
          <Route path="clients" element={<C2Clients />} />
          <Route path="clients/:tenantId" element={<C2ClientDetail />} />
          <Route path="clients/:tenantId/police/:policeId" element={<PoliceDetail />} />
          <Route path="sinistres" element={<C2SinistresWrapper />} />
          <Route path="sinistre/:id" element={<C2SinistreDetail />} />
          <Route path="sinistres/:id" element={<C2SinistreDetail />} />
          <Route path="pre-declarations" element={<PreOuverturesList tenants={tenants} profile={profile} />} />
          <Route path="pre-declarations/new" element={<GestionnairePreDecl tenants={tenants} />} />
          <Route path="pre-declarations/:id" element={<PreDeclarationV4Detail tenants={tenants} />} />
          <Route path="relances" element={<RelancesDashboard />} />
          {/* Legacy routes — redirect */}
          <Route path="pre-ouvertures" element={<Navigate to="/c2/pre-declarations" replace />} />
          <Route path="pre-ouvertures/:id" element={<PreOuvertureDetail tenants={tenants} />} />
          <Route path="dashboard" element={<Navigate to="/c2/clients" replace />} />
          <Route path="*" element={<Navigate to="/c2/clients" replace />} />
        </Routes>
      </main>
    </div>
  )
}
